import React from 'react'
import {observer} from 'mobx-react-lite'
import {makeOcStyles, syskronPalette, getHoverStyle} from '@oc/styles'
import {Typography, Grid, IconButton, ListSubheader} from '@mui/material'
import {
    useCustomerFromRoute,
    fullPath,
    fullPathWithParams,
    useCustomerRouteContext,
    useValidationState,
    useUserPermissionsContext
} from '@oc/shell-core'
import {DoubleArrowLeftIcon, DoubleArrowRightIcon, EnterpriseIcon, ReadyKitIcon, OperationsConsoleLogo} from '@oc/icons'
import routes from '../routes'
import {useAppDrawerContext, useIsAppDrawerExpanded} from '@oc/shell-core'
import {messages} from '../translations'
import {ocAppBarHeight} from '../AppBar/OcAppBar'
import {RkLastDataChange} from './RkLastDataChange/RkLastDataChange'

const useStyles = makeOcStyles(theme => ({
    root: {
        padding: theme.spacing(1),
        background: theme.syskronPalette.colors.P900,
        color: theme.syskronPalette.contrastColors.P900,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: ocAppBarHeight,
        textDecoration: 'none',
        cursor: 'pointer'
    },
    title: {
        fontWeight: 700,
        size: '20px',
        lineHeight: '20px',
        whiteSpace: 'break-spaces',
        flexGrow: 1
    },
    ocIcon: {
        marginRight: theme.spacing(1)
    },
    expandButton: {
        ...getHoverStyle(undefined, theme.syskronPalette.colors.P700),
        padding: '5px',
        alignSelf: 'end'
    },
    subheader: {
        minHeight: '60px',
        lineHeight: '1rem',
        padding: theme.spacing(1),
        display: 'flex',
        alignItems: 'center'
    },
    accountSubheader: {
        backgroundColor: theme.syskronPalette.colors.P800,
        color: theme.syskronPalette.contrastColors.P800,
        textDecoration: 'none',
        cursor: 'pointer',
        marginTop: '1px'
    },
    rkSubheader: {
        justifyContent: 'space-between',
        backgroundColor: theme.syskronPalette.colors.P700,
        color: theme.syskronPalette.contrastColors.P700,
        marginTop: '2px'
    },
    noRightPadding: {
        paddingRight: 0
    },
    subheaderTypo: {
        overflowWrap: 'anywhere',
        fontWeight: 700
    },
    subheaderIcon: {
        marginRight: theme.spacing(1)
    },
    changesDialogButton: {
        padding: theme.spacing(1)
    }
}))

function _Header() {
    const {classes} = useStyles()
    const {menuExpanded, setMenuExpanded} = useAppDrawerContext()
    const validationState = useValidationState()
    const routeDashboard = fullPath(routes.allOther.dashboard, routes)

    const toggleMenuExpand = React.useCallback(
        (e: React.SyntheticEvent) => {
            e.preventDefault()
            e.stopPropagation()
            const newValue = !menuExpanded
            setMenuExpanded(newValue)
        },
        [menuExpanded, setMenuExpanded]
    )

    const navigateToAccounts = React.useCallback(
        (e: React.SyntheticEvent) => {
            e.preventDefault()
            validationState.pushToHistory(routeDashboard)
        },
        [validationState, routeDashboard]
    )

    return (
        <Grid
            component="a"
            href={routeDashboard}
            className={classes.root}
            onClick={navigateToAccounts}
            title={messages.getText('headers', 'operationsConsoleTooltip')}
        >
            <OperationsConsoleLogo size="40px" className={menuExpanded ? classes.ocIcon : undefined} />
            {menuExpanded && (
                <Typography variant="overline" className={classes.title}>
                    {messages.getText('headers', 'operationsConsole')}
                </Typography>
            )}
            <IconButton
                disableTouchRipple={true}
                onClick={toggleMenuExpand}
                className={classes.expandButton}
                title={menuExpanded ? 'Collapse' : 'Expand'}
                size="large"
            >
                {menuExpanded ? (
                    <DoubleArrowLeftIcon size="16px" htmlColor={syskronPalette.colors.P50} />
                ) : (
                    <DoubleArrowRightIcon size="16px" htmlColor={syskronPalette.colors.P50} />
                )}
            </IconButton>
        </Grid>
    )
}
export const Header = observer(_Header)

function _RkSubHeader() {
    const {classes, cx} = useStyles()
    const {loadingCustomerLevel: loading, loadingRkLevel, rkName} = useCustomerRouteContext()
    const {customerId, rkId} = useCustomerFromRoute()
    const userPermissions = useUserPermissionsContext()
    const canViewAccount = customerId ? userPermissions.accounts.canView(customerId) : false
    const menuExpanded = useIsAppDrawerExpanded()

    return (
        <ListSubheader
            disableSticky
            className={cx(classes.subheader, classes.rkSubheader)}
            title={menuExpanded ? undefined : rkName}
        >
            <div style={{display: 'flex'}}>
                <ReadyKitIcon size="24px" className={classes.subheaderIcon} />
                {menuExpanded && (
                    <Typography className={classes.subheaderTypo} variant="body1">
                        {loading || loadingRkLevel ? 'loading...' : rkName}
                    </Typography>
                )}
            </div>
            {customerId && rkId && canViewAccount ? (
                <RkLastDataChange accountId={customerId} readykitId={rkId} />
            ) : null}
        </ListSubheader>
    )
}
export const RkSubHeader = observer(_RkSubHeader)

function _AccountSubHeader() {
    const {classes, cx} = useStyles()
    const {desiredAccountId, accountName, loadingCustomerLevel: loading} = useCustomerRouteContext()
    const {customerId} = useCustomerFromRoute()
    const validationState = useValidationState()
    const routeCustomer = fullPathWithParams(routes.withCustomerOnly.customerEdit, routes, customerId)
    const menuExpanded = useIsAppDrawerExpanded()

    const navigateToCustomer = React.useCallback(
        (e: React.SyntheticEvent) => {
            e.preventDefault()

            if (!customerId) {
                return
            }

            validationState.pushToHistory(routeCustomer)
        },
        [validationState, routeCustomer, customerId]
    )

    return (
        <ListSubheader
            disableSticky
            className={cx(classes.subheader, classes.accountSubheader)}
            title={menuExpanded ? undefined : `${accountName} (${desiredAccountId})`}
            onClick={navigateToCustomer}
            component="a"
            href={routeCustomer}
        >
            <EnterpriseIcon size="24px" className={classes.subheaderIcon} />
            {menuExpanded && (
                <Typography className={classes.subheaderTypo} variant="body1">
                    {!loading && accountName
                        ? `${accountName} (${desiredAccountId})`
                        : `loading... (${desiredAccountId})`}
                </Typography>
            )}
        </ListSubheader>
    )
}
export const AccountSubHeader = observer(_AccountSubHeader)
